import { useEffect, useRef, useState, type RefObject } from "react";
import { createPortal } from "react-dom";
import { Link, Outlet, useLocation } from "react-router-dom";
import { BasilX } from "@/components/icons/basil";
import { BrandWordmark } from "@/components/layout/BrandWordmark";
import { HudChips } from "@/components/layout/HudChips";
import { ProfileChip } from "@/components/layout/ProfileChip";
import { SeasonCountdownBar } from "@/components/layout/SeasonCountdownBar";
import { Countdown } from "@/components/renewal/Countdown";
import { MobileTabBar } from "@/components/renewal/MobileTabBar";
import { NotificationCenter } from "@/components/renewal/NotificationCenter";
import { SeasonPhaseBanner } from "@/components/renewal/SeasonPhaseBanner";
import { SideNav } from "@/components/layout/SideNav";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { TopBar } from "@/components/layout/TopBar";
import { SEASON_CONFIG, seasonPhase, type SeasonPhase } from "@/lib/season";
import { startLenis } from "@/lib/smoothScroll";
import { cn } from "@shared/lib/utils";

/**
 * The frame every tycoonex.com page lives in.
 * lg+: fixed 216px sidebar (wordmark + vertical nav + CTA/profile footer) on
 * --ty-bg-0, content column offset by pl-[216px] under a sticky h-12 slim bar
 * (season chip left, ProfileChip right). <lg: sticky TopBar with a hamburger
 * that opens the portal drawer (backdrop, ESC, body scroll lock) + season
 * banners + bottom tab bar. Surfaces are flat: hairline dividers + lightness
 * steps, no blur.
 */
export function AppShell() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  // Hamburger button in TopBar — focus returns here when the drawer closes.
  const menuTriggerRef = useRef<HTMLButtonElement>(null);

  // Route change (e.g. browser back) always closes the drawer.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Lenis smooth scroll (scroll-asset-prd §5) — shell-wide, reduced-motion skips.
  useEffect(() => startLenis(), []);

  return (
    <div className="ty-canvas flex min-h-dvh w-full flex-col lg:pl-[216px]">
      {/* lg+ fixed sidebar — lowest surface step, 1px hairline on the right */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-[216px] flex-col border-r border-line bg-(--ty-bg-0) lg:flex">
        <div className="shrink-0 px-5 pb-4 pt-6">
          <BrandWordmark imgClassName="h-5.5" />
        </div>
        <SideNav variant="sidebar" />
      </aside>

      {/* <lg chrome — `contents` keeps the bars' own sticky behavior intact */}
      <TopBar
        onOpenMenu={() => setDrawerOpen(true)}
        menuOpen={drawerOpen}
        menuButtonRef={menuTriggerRef}
      />
      <div className="contents lg:hidden">
        <SeasonPhaseBanner />
        <SeasonCountdownBar />
      </div>

      {/* lg+ slim bar — season chip left, game HUD(잔액·무료팩·미션) + profile right */}
      <div className="sticky top-0 z-40 hidden h-12 shrink-0 items-center gap-4 border-b border-line bg-(--ty-bg) px-6 lg:flex">
        <SeasonSlimChip />
        <div className="ml-auto flex items-center gap-2">
          <HudChips />
          <NotificationCenter />
          <ProfileChip />
        </div>
      </div>

      <main className="relative z-10 flex w-full flex-1 flex-col">
        {/* 모바일: 하단 탭바(52px+safe-area)와 겹치지 않도록 pb 확장 */}
        <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col px-4 pb-28 pt-6 sm:px-6 sm:pt-10 lg:pb-16">
          <Outlet />
        </div>
      </main>

      <SiteFooter />
      <MobileTabBar />

      <MobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        triggerRef={menuTriggerRef}
      />
    </div>
  );
}

/** Slim-bar phase copy — inline compaction of SeasonPhaseBanner (labels mirror its PHASE_COPY). */
const SLIM_PHASE: Record<SeasonPhase, { label: string; tone: string }> = {
  prep: { label: "Season Prep", tone: "text-ink-dim" },
  live: { label: "Season Live", tone: "text-accent" },
  snapshot: { label: "Snapshot Soon", tone: "text-yellow" },
  settlement: { label: "Settlement", tone: "text-ink-dim" },
};

/** lg+ slim-bar season chip — phase label + one countdown chip, links to /season. */
function SeasonSlimChip() {
  const phase = seasonPhase();
  const copy = SLIM_PHASE[phase];
  const showCountdown = (phase === "live" || phase === "snapshot") && SEASON_CONFIG.endsAt;

  return (
    <Link
      to="/season"
      aria-label={`${SEASON_CONFIG.label}: ${copy.label}`}
      className="flex min-w-0 items-center gap-2.5 rounded-sm outline-none focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-accent/60"
    >
      <span
        className={cn(
          "truncate font-mono text-[10px] font-semibold uppercase tracking-[0.16em]",
          copy.tone,
        )}
      >
        {SEASON_CONFIG.label} · {copy.label}
      </span>
      {showCountdown ? (
        <Countdown
          to={SEASON_CONFIG.endsAt!}
          className="shrink-0 rounded-full border border-line px-2 py-0.5 font-mono text-[10px] tabular-nums text-ink-dim"
        />
      ) : null}
    </Link>
  );
}

type MobileDrawerProps = {
  open: boolean;
  onClose: () => void;
  /** The hamburger button that opened the drawer — refocused on close. */
  triggerRef: RefObject<HTMLButtonElement | null>;
};

function MobileDrawer({ open, onClose, triggerRef }: MobileDrawerProps) {
  const asideRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  // Focus management: close button takes focus on open; the hamburger trigger
  // gets it back on close. Tab is trapped inside the aside while open.
  useEffect(() => {
    if (!open) return;
    closeButtonRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const aside = asideRef.current;
      if (!aside) return;
      const focusables = Array.from(
        aside.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el.tabIndex >= 0);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (event.shiftKey) {
        if (active === first || !aside.contains(active)) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last || !aside.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      triggerRef.current?.focus();
    };
  }, [open, triggerRef]);

  return createPortal(
    <div className="lg:hidden">
      <div
        className={cn(
          "fixed inset-0 z-[60] bg-black/65 transition-opacity duration-300",
          open ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0",
        )}
        aria-hidden
        onClick={onClose}
      />

      <aside
        ref={asideRef}
        role="dialog"
        aria-modal="true"
        aria-label="TycoonEX menu"
        aria-hidden={!open}
        className={cn(
          "fixed inset-y-0 left-0 z-[70] flex w-[min(100vw-3.5rem,300px)] flex-col border-r border-line bg-(--ty-bg-2) shadow-[16px_0_48px_-12px_rgba(0,0,0,0.85)] transition-transform duration-300 ease-(--ty-ease)",
          open ? "translate-x-0" : "pointer-events-none -translate-x-full",
        )}
      >
        <div className="flex h-16 shrink-0 items-center justify-between border-b border-line px-5">
          <BrandWordmark
            imgClassName="h-5.5"
            onClick={onClose}
            tabIndex={open ? 0 : -1}
          />
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            tabIndex={open ? 0 : -1}
            className="grid size-8 place-items-center rounded-full border border-line bg-white/4 text-ink-dim transition-colors duration-200 hover:border-line-hi hover:text-ink"
          >
            <BasilX className="size-4" />
          </button>
        </div>
        <SideNav tabbable={open} onNavigate={onClose} />
      </aside>
    </div>,
    document.body,
  );
}
